import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { DropdownItem } from 'src/componentes/dropdown';

import Alert from 'src/componentes/message-box/alert';
import Confirm from 'src/componentes/message-box/confirm';
import CriticalConfirm from 'src/componentes/message-box/critical-confirm';
import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import GerarRespostasTesteCargaDialog from 'src/componentes/formulario-global/GerarRespostasTesteCargaDialog';
import List from 'src/componentes/list';
import ListColumn from 'src/componentes/list/list-column';
import LucideIcon from 'src/componentes/lucide-icon';
import StatusBadge from 'src/componentes/StatusBadge';
import useAuth from 'src/hooks/useAuth';
import { ParseError } from 'src/services/downloadFileUrl';
import http from 'src/services/httpService';
import { COLUNAS, FILTROS, STATUS_PESQUISA_CLIMA } from 'src/utils/constants';
import errorHandler from 'src/utils/error-handler';
import formatDateFns from 'src/utils/formatDateFns';
import { capitalizeFirstLetter } from 'src/utils/string';
import { getPesquisaClimaLink } from '../utils';
import PesquisaClimaEditView from '../PesquisaClimaEditView';
import Search from './Search';
import SearchSchema from './SearchSchema';

function PesquisaClimaListView(props) {
  const intl = useIntl();
  const { terms: resources, user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);
  const [forceRefresh, setForceRefresh] = useState(0);

  const handleLinkHistoricoExportacao = () => {
    window.open('/ferramentas/historico-exportacaojob', '_blank');
  };

  const renderMensagemExportacaoEmSegundoPlano = () => {
    ContentManager.addContent(
      <Alert
        closeModal
        title={intl.formatMessage({ id: 'exportacao' })}
        message={
          <div>
            <div>
              {intl.formatMessage({
                id: 'exportacaoEstaSendoProcessadaEmSegundoPlano',
              })}
            </div>
            <div className="d-flex align-items-center mt-2">
              <div className="mr-2">
                {intl.formatMessage({
                  id: 'vocePodeAcompanharAndamentoFazerDownloadNo',
                })}
                :
              </div>
              <Button
                color="outline-primary"
                leftIcon={ExternalLink}
                onClick={handleLinkHistoricoExportacao}
              >
                {intl.formatMessage({ id: 'acompanharExportacao' })}
              </Button>
            </div>
          </div>
        }
      />
    );
  };

  const handleExportarRespostas = async (item) => {
    try {
      await http.post('/FormularioGlobalRespostas/ExportarRespostas', {
        entidadeId: item?.id,
      });

      renderMensagemExportacaoEmSegundoPlano();
    } catch (err) {
      errorHandler(ParseError(err));
    }
  };

  const handleCancelarPesquisaConfirm = async (item) => {
    try {
      await http.post('/PesquisaClima/Cancelar', {
        id: item.id,
        parameters: props.parameters,
      });

      enqueueSnackbar(
        intl.formatMessage({ id: 'labelPesquisaClimaCanceladaComSucesso' }),
        { variant: 'success' }
      );

      setForceRefresh((prev) => prev + 1);
    } catch (err) {
      errorHandler(err);
    }
  };

  const handleCancelarPesquisa = (item) => {
    ContentManager.addContent(
      <CriticalConfirm
        title={intl.formatMessage({ id: 'labelCancelarPesquisaClima' })}
        isFunction
        message={() => (
          <div>
            <p>
              {intl.formatMessage(
                { id: 'labelCancelarPesquisaClimaIntro' },
                { nome: item?.nome }
              )}
            </p>
            <p>
              {intl.formatMessage({ id: 'labelCancelarPesquisaClimaDesc1' })}
            </p>
            <p>
              {intl.formatMessage({ id: 'labelCancelarPesquisaClimaDesc2' })}
            </p>
            <p>
              {intl.formatMessage({ id: 'labelCancelarPesquisaClimaDesc3' })}
            </p>
          </div>
        )}
        labelConfirm={intl.formatMessage({ id: 'labelConfirmarCancelamento' })}
        handleConfirm={() => handleCancelarPesquisaConfirm(item)}
      />
    );
  };

  const handleEncerrarPesquisaConfirm = async (item) => {
    try {
      await http.post('/PesquisaClima/Encerrar', {
        id: item.id,
        parameters: props.parameters,
      });

      enqueueSnackbar(
        intl.formatMessage(
          { id: 'labelPesquisaClimaEncerradaComSucesso' },
          { pesquisa: resources.pesquisa }
        ),
        { variant: 'success' }
      );

      setForceRefresh((prev) => prev + 1);
    } catch (err) {
      errorHandler(err);
    }
  };

  const handleEncerrarPesquisa = (item) => {
    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage({
          id: 'labelEncerrarPesquisaClimaConfirmacao',
        })}
        handleConfirm={() => handleEncerrarPesquisaConfirm(item)}
      />
    );
  };

  const handleClonarPesquisaConfirm = async (item) => {
    try {
      await http.post('/PesquisaClima/Clonar', {
        id: item.id,
        parameters: props.parameters,
      });

      enqueueSnackbar(
        intl.formatMessage(
          { id: 'labelPesquisaClimaClonadaComSucesso' },
          { pesquisa: resources.pesquisa }
        ),
        { variant: 'success' }
      );

      setForceRefresh((prev) => prev + 1);
    } catch (err) {
      errorHandler(err);
    }
  };

  const handleClonarPesquisa = (item) => {
    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage(
          { id: 'labelClonarPesquisaClimaConfirmacao' },
          { pesquisa: resources.pesquisa }
        )}
        handleConfirm={() => handleClonarPesquisaConfirm(item)}
      />
    );
  };

  const handleGerarRespostasTesteCarga = (item) => {
    ContentManager.addContent(
      <GerarRespostasTesteCargaDialog
        entidadeId={item?.id}
        entidadeLabel={capitalizeFirstLetter(resources.pesquisa)}
        entidadeNome={item?.nome}
        exibirOpcaoVariarColaboradores
        variarColaboradoresLabel={intl.formatMessage({
          id: 'labelVariarColaboradoresParticipantesPesquisa',
        })}
      />
    );
  };

  const renderMenu = (item) => {
    const link = getPesquisaClimaLink(item);
    const isRascunho = item?.status?.id === STATUS_PESQUISA_CLIMA.RASCUNHO;

    return (
      <>
        {item?.podeClonar && (
          <DropdownItem onClick={() => handleClonarPesquisa(item)}>
            <LucideIcon icon="Copy" />
            {intl.formatMessage({ id: 'labelClonarPesquisaClima' })}
          </DropdownItem>
        )}
        {!isRascunho && (
          <DropdownItem onClick={() => handleExportarRespostas(item)}>
            <LucideIcon icon="Download" />
            {intl.formatMessage({ id: 'labelExportarRespostas' })}
          </DropdownItem>
        )}
        {link && (
          <DropdownItem onClick={() => window.open(link, '_blank')}>
            <LucideIcon icon="ExternalLink" />
            {intl.formatMessage({ id: 'labelAbrirLinkPesquisa' })}
          </DropdownItem>
        )}
        {item?.podeGerarRespostasTesteCarga && (
          <DropdownItem onClick={() => handleGerarRespostasTesteCarga(item)}>
            <LucideIcon icon="Play" />
            {intl.formatMessage({ id: 'labelGerarRespostasTesteCarga' })}
          </DropdownItem>
        )}
        {item?.podeEncerrar && (
          <DropdownItem onClick={() => handleEncerrarPesquisa(item)}>
            <LucideIcon icon="CircleX" />
            {intl.formatMessage({ id: 'labelEncerrarPesquisaClima' })}
          </DropdownItem>
        )}
        {item?.podeCancelar && (
          <DropdownItem onClick={() => handleCancelarPesquisa(item)}>
            <LucideIcon icon="Ban" />
            {intl.formatMessage({ id: 'labelCancelarPesquisaClima' })}
          </DropdownItem>
        )}
      </>
    );
  };

  const renderNome = (item, handleEditClose) => {
    const handleClick = () => {
      ContentManager.addContent(
        <PesquisaClimaEditView item={item} handleClose={handleEditClose} />
      );
    };

    return (
      <button
        type="button"
        className="btn btn-link p-0 text-left"
        onClick={handleClick}
      >
        {item?.nome}
      </button>
    );
  };

  const renderResponsaveis = (item) => {
    const responsaveis = item?.responsaveis;

    if (Array.isArray(responsaveis)) {
      return responsaveis.map((responsavel) => responsavel.nome).join(', ');
    }

    if (responsaveis?.nome) return responsaveis.nome;

    return responsaveis;
  };

  const renderData = (data) =>
    data
      ? format(new Date(data), currentDisplay, { locale: currentLocale })
      : '-';

  const renderDataInicio = (item) => renderData(item?.dataInicio);

  const renderDataTermino = (item) => renderData(item?.dataTermino);

  const renderTipoIdentificacao = (item) =>
    item?.tipoIdentificacao?.descricao || '-';

  const renderStatus = (item) => {
    const status = item?.status;

    if (!status?.descricao) return null;

    return (
      <StatusBadge
        text={capitalizeFirstLetter(
          intl.formatMessage({ id: status.descricao })
        )}
        textColor={status.corFundo ? status.cor : undefined}
        backgroundColor={status.corFundo || status.cor}
      />
    );
  };

  return (
    <List
      url="/PesquisaClima"
      editComponent={PesquisaClimaEditView}
      tag={COLUNAS.PESQUISA_CLIMA}
      filtroId={FILTROS.PESQUISA_CLIMA}
      showSearch
      filterDrawer
      searchSchema={SearchSchema}
      searchComponent={<Search />}
      renderMenuAddOptions
      renderMenu={renderMenu}
      {...props}
      forceRefresh={forceRefresh}
    >
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'idIntegracao' })
        )}
        valueField="idIntegracao"
        sortField="idIntegracao"
      />
      <ListColumn
        headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'nome' }))}
        valueField="nome"
        valueFunction={renderNome}
        sortField="nome"
        required
      />
      <ListColumn
        headerText={capitalizeFirstLetter(resources.area)}
        valueField="area"
        sortField="area"
      />
      <ListColumn
        headerText={capitalizeFirstLetter(resources.responsaveis)}
        valueField="responsaveis"
        valueFunction={renderResponsaveis}
        required
      />
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'descricao' })
        )}
        valueField="descricao"
        sortField="descricao"
      />
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'dataInicio' })
        )}
        valueField="dataInicio"
        valueFunction={renderDataInicio}
        sortField="dataInicio"
      />
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'dataTermino' })
        )}
        valueField="dataTermino"
        valueFunction={renderDataTermino}
        sortField="dataTermino"
      />
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'labelTipoPesquisa' })
        )}
        valueField="tipoIdentificacao"
        valueFunction={renderTipoIdentificacao}
        sortField="tipoIdentificacao"
      />
      <ListColumn
        headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'status' }))}
        valueField="status"
        valueFunction={renderStatus}
        sortField="status"
      />
    </List>
  );
}

export default PesquisaClimaListView;
